import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button } from "../components/ui";
import { ConfirmModal } from "../components/ui/Modal";
import useResizableColumns from "../hooks/useResizableColumns";

function InterventiListPage() {
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();
  const [interventi, setInterventi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsaFilter, setRsaFilter] = useState("");
  const [fornitoreFilter, setFornitoreFilter] = useState("");
  const [tipologiaFilter, setTipologiaFilter] = useState("");
  const [validatoFilter, setValidatoFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");
  const [rsaList, setRsaList] = useState([]);
  const [fornitoriList, setFornitoriList] = useState([]);
  const [tipologieList, setTipologieList] = useState([]);
  const [showNewModal, setShowNewModal] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const isFornitore = user?.ruolo === 100000005;
  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const isDirezione = user?.ruolo === 100000002;
  const canCreateIntervento = isFornitore || isAdmin || isRspp || isDirezione;

  const { onMouseDown, headerStyle, cellStyle } = useResizableColumns({
    data: 100,
    nome: 220,
    rsa: 180,
    fornitore: 160,
    tipologia: 180,
    stato: 100,
    azioni: 120,
  });

  useEffect(() => {
    // Fetch RSA (da anagrafica, non cambia con i filtri)
    if (!isFornitore) {
      apiRequest("/rsa")
        .then((res) => setRsaList(res?.value || []))
        .catch(() => setRsaList([]));
    }
  }, [apiRequest, isFornitore]);

  // Fetch fornitori e tipologie disponibili (da storico interventi), ricarica quando cambia RSA
  useEffect(() => {
    const params = new URLSearchParams();
    if (rsaFilter) params.append("rsaId", rsaFilter);
    const qs = params.toString();

    const promises = [
      apiRequest(`/interventi/tipologie-disponibili?${qs}`).catch(() => ({
        value: [],
      })),
    ];
    if (!isFornitore) {
      promises.push(
        apiRequest(`/interventi/fornitori-disponibili?${qs}`).catch(() => ({
          value: [],
        })),
      );
    }

    Promise.all(promises).then(([tipRes, fornRes]) => {
      const tipologie = tipRes?.value || [];
      setTipologieList(tipologie);
      setTipologiaFilter((prev) => {
        if (prev && !tipologie.some((t) => t.id === prev)) return "";
        return prev;
      });

      if (!isFornitore && fornRes) {
        const fornitori = fornRes?.value || [];
        setFornitoriList(fornitori);
        setFornitoreFilter((prev) => {
          if (prev && !fornitori.some((f) => f.id === prev)) return "";
          return prev;
        });
      }
    });
  }, [apiRequest, rsaFilter, isFornitore]);

  useEffect(() => {
    const fetchInterventi = async () => {
      try {
        const params = new URLSearchParams();
        if (rsaFilter) params.append("rsaId", rsaFilter);
        if (fornitoreFilter) params.append("fornitoreId", fornitoreFilter);
        if (tipologiaFilter)
          params.append("tipologiaInterventoId", tipologiaFilter);
        if (validatoFilter !== "") params.append("validato", validatoFilter);

        const result = await apiRequest(`/interventi?${params.toString()}`);
        setInterventi(result.value || []);
      } catch (error) {
        console.error("Error fetching interventi:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInterventi();
  }, [apiRequest, rsaFilter, fornitoreFilter, tipologiaFilter, validatoFilter]);

  const loadData = async () => {
    try {
      const params = new URLSearchParams();
      if (rsaFilter) params.append("rsaId", rsaFilter);
      if (fornitoreFilter) params.append("fornitoreId", fornitoreFilter);
      if (tipologiaFilter)
        params.append("tipologiaInterventoId", tipologiaFilter);
      if (validatoFilter !== "") params.append("validato", validatoFilter);
      const result = await apiRequest(`/interventi?${params.toString()}`);
      setInterventi(result.value || []);
    } catch (error) {
      console.error("Error fetching interventi:", error);
    }
  };

  const filteredInterventi = useMemo(() => {
    let result = interventi;
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      result = result.filter((i) =>
        [
          i.nome,
          i.rsa?.nome,
          i.fornitore?.nome,
          i.tipologiaIntervento,
          i.nomeTecnico,
        ].some((v) => v && v.toLowerCase().includes(q)),
      );
    }
    if (dataDa) {
      const da = new Date(dataDa);
      result = result.filter((i) => new Date(i.dataIntervento) >= da);
    }
    if (dataA) {
      const a = new Date(dataA);
      a.setHours(23, 59, 59, 999);
      result = result.filter((i) => new Date(i.dataIntervento) <= a);
    }
    return result;
  }, [interventi, searchText, dataDa, dataA]);

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    setDeleteLoading(true);
    try {
      await apiRequest(`/interventi/${itemToDelete.id}?force=true`, {
        method: "DELETE",
      });
      showToast("Intervento eliminato con successo", "success");
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadData();
    } catch (error) {
      console.error("Error deleting intervento:", error);
      showToast(error.message || "Errore durante l'eliminazione", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Registro Interventi
        </h1>
        {canCreateIntervento && (
          <Link to="/interventi/nuovo" className="btn btn-primary">
            + Nuovo Intervento
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex gap-4 flex-wrap">
          {!isFornitore && (
            <select
              value={rsaFilter}
              onChange={(e) => setRsaFilter(e.target.value)}
              className="input w-48"
            >
              <option value="">Tutte le RSA</option>
              {rsaList.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nome}
                </option>
              ))}
            </select>
          )}
          {!isFornitore && (
            <select
              value={fornitoreFilter}
              onChange={(e) => setFornitoreFilter(e.target.value)}
              className="input w-48"
            >
              <option value="">Tutti i fornitori</option>
              {fornitoriList.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome}
                </option>
              ))}
            </select>
          )}
          <select
            value={tipologiaFilter}
            onChange={(e) => setTipologiaFilter(e.target.value)}
            className="input w-56"
          >
            <option value="">Tutte le tipologie</option>
            {tipologieList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nome}
              </option>
            ))}
          </select>
          <select
            value={validatoFilter}
            onChange={(e) => setValidatoFilter(e.target.value)}
            className="input w-52"
          >
            <option value="">Tutti gli stati</option>
            <option value="false">Da validare</option>
            <option value="true">Validati</option>
            <option value="azione_correttiva">Azione correttiva</option>
          </select>
        </div>
        <div className="flex gap-4 flex-wrap items-center mt-4">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Cerca per nome, RSA, fornitore, tipologia, tecnico..."
            className="input w-80"
          />
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">Dal</label>
            <input
              type="date"
              value={dataDa}
              onChange={(e) => setDataDa(e.target.value)}
              className="input w-40"
            />
            <label className="text-sm text-gray-500">Al</label>
            <input
              type="date"
              value={dataA}
              onChange={(e) => setDataA(e.target.value)}
              className="input w-40"
            />
          </div>
          {(searchText || dataDa || dataA) && (
            <button
              onClick={() => {
                setSearchText("");
                setDataDa("");
                setDataA("");
              }}
              className="text-sm text-primary-600 hover:underline"
            >
              Azzera ricerca
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card text-center">
          <div className="text-2xl font-bold">{filteredInterventi.length}</div>
          <div className="text-sm text-gray-500">Totale Interventi</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-green-600">
            {
              filteredInterventi.filter(
                (i) =>
                  i.statoValidazione === 100000001 ||
                  i.statoValidazione === 100000003 ||
                  (i.validato && i.statoValidazione === undefined),
              ).length
            }
          </div>
          <div className="text-sm text-gray-500">Validati</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-yellow-600">
            {
              filteredInterventi.filter(
                (i) =>
                  i.statoValidazione === 100000000 ||
                  (!i.validato && i.statoValidazione === undefined),
              ).length
            }
          </div>
          <div className="text-sm text-gray-500">Da Validare</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-red-600">
            {
              filteredInterventi.filter((i) => i.statoValidazione === 100000002)
                .length
            }
          </div>
          <div className="text-sm text-gray-500">Azione Correttiva</div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {filteredInterventi.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table" style={{ tableLayout: "fixed" }}>
              <thead>
                <tr>
                  {[
                    { key: "data", label: "Data" },
                    { key: "nome", label: "Nome" },
                    { key: "rsa", label: "RSA" },
                    ...(!isFornitore
                      ? [{ key: "fornitore", label: "Fornitore" }]
                      : []),
                    { key: "tipologia", label: "Tipologia" },
                    { key: "stato", label: "Stato" },
                    { key: "azioni", label: "Azioni" },
                  ].map((col) => (
                    <th key={col.key} style={headerStyle(col.key)}>
                      <div className="flex items-center justify-between">
                        <span>{col.label}</span>
                        <span
                          onMouseDown={(e) => {
                            onMouseDown(col.key, e);
                            e.target.style.background = "#3b82f6";
                          }}
                          style={{
                            position: "absolute",
                            right: -1,
                            top: 4,
                            bottom: 4,
                            width: 3,
                            cursor: "col-resize",
                            borderRadius: 2,
                            background: "#d1d5db",
                            transition: "background 0.15s, width 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = "#3b82f6";
                            e.target.style.width = "4px";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = "#d1d5db";
                            e.target.style.width = "3px";
                          }}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredInterventi.map((i) => (
                  <tr key={i.id}>
                    <td style={cellStyle("data")}>
                      {new Date(i.dataIntervento).toLocaleDateString("it-IT")}
                    </td>
                    <td
                      style={cellStyle("nome")}
                      className="font-medium"
                      title={i.nome}
                    >
                      {i.nome}
                    </td>
                    <td style={cellStyle("rsa")} title={i.rsa?.nome}>
                      {i.rsa?.nome || "-"}
                    </td>
                    {!isFornitore && (
                      <td
                        style={cellStyle("fornitore")}
                        title={i.fornitore?.nome}
                      >
                        {i.fornitore?.nome || "-"}
                      </td>
                    )}
                    <td
                      style={cellStyle("tipologia")}
                      title={i.tipologiaIntervento}
                    >
                      {i.tipologiaIntervento || "-"}
                    </td>
                    <td style={cellStyle("stato")}>
                      {i.statoValidazione === 100000002 ? (
                        <span className="badge-danger">Azione correttiva</span>
                      ) : i.statoValidazione === 100000003 ? (
                        <span
                          className="badge-success"
                          title="Validato dopo azione correttiva"
                        >
                          Validato (AC)
                        </span>
                      ) : i.statoValidazione === 100000001 ||
                        (i.validato && i.statoValidazione === undefined) ? (
                        <span className="badge-success">Validato</span>
                      ) : (
                        <span className="badge-warning">Da validare</span>
                      )}
                    </td>
                    <td style={cellStyle("azioni")}>
                      <div className="flex gap-2">
                        <Link
                          to={`/interventi/${i.id}`}
                          className="text-primary-600 hover:underline"
                        >
                          Dettagli
                        </Link>
                        {isAdmin && (
                          <button
                            onClick={() => confirmDelete(i)}
                            className="text-red-600 hover:text-red-800"
                            title="Elimina"
                          >
                            Elimina
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">
            Nessun intervento trovato
          </p>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Elimina Intervento"
        message={`Sei sicuro di voler eliminare l'intervento "${itemToDelete?.nome}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
}

export default InterventiListPage;
